import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { X, CheckCircle2, FileText, Handshake, MessageSquare, Wallet, Archive, ListChecks, Coins, Wheat } from "lucide-react";
import { api } from "../lib/api";
import { Button } from "./ui/button";
import PurchaseChat from "./PurchaseChat";
import UpiPayPanel from "./UpiPayPanel";
import PurchaseVault from "./PurchaseVault";

const WIZARD_META = {
  gold: {
    title: "Buying physical gold, safely",
    Icon: Coins,
    accent: "text-amber_brand",
    bg: "bg-amber_brand-bg",
    ring: "ring-amber_brand/30",
  },
  silver: {
    title: "Buying physical silver, safely",
    Icon: Coins,
    accent: "text-slate-500",
    bg: "bg-slate-100",
    ring: "ring-slate-300/60",
  },
  land: {
    title: "Buying agri land, safely",
    Icon: Wheat,
    accent: "text-moss",
    bg: "bg-moss-bg/50",
    ring: "ring-moss/30",
  },
};

const GOLD_STEPS = [
  {
    title: "Lock today's rate",
    why: "Jeweller board rates vary city to city — never pay above the day's benchmark.",
    checkpoints: [
      "Compare the shop's board rate with today's city rate shown on this page (±1%)",
      "Confirm rate is quoted per gram for the exact purity (22K vs 24K)",
      "Ask making charges & GST upfront, in writing",
    ],
    documents: [],
  },
  {
    title: "Verify the seller",
    why: "Only BIS-registered jewellers can legally sell hallmarked gold in India.",
    checkpoints: [
      "Ask for the shop's BIS registration number",
      "Verify GSTIN on the display board matches the invoice later",
      "Check reviews / years in business for a new shop",
    ],
    documents: ["BIS registration certificate", "GSTIN"],
  },
  {
    title: "Verify the metal",
    why: "Hallmark + HUID is your legal proof of purity.",
    checkpoints: [
      "Check hallmark: BIS logo + purity (916 = 22K, 999 = 24K) + 6-digit HUID",
      "Verify the HUID in the BIS Care app (free) before paying",
      "Weigh the piece in front of you on the shop's certified scale",
      "For bars/coins: sealed pack with assay certificate",
    ],
    documents: ["Assay certificate (bars/coins)", "HUID number"],
  },
  {
    title: "Negotiate charges",
    why: "Making charges are fully negotiable — this is where shops earn.",
    checkpoints: [
      "Coins/bars: target 0-4% making; jewellery: negotiate 8-14% down",
      "Refuse any 'wastage' charge on machine-made pieces",
      "Get the buyback policy (rate & deductions) in writing",
    ],
    documents: ["Written buyback policy"],
  },
  {
    title: "Pay & collect the invoice",
    why: "A proper GST invoice is what makes your gold sellable later at full value.",
    checkpoints: [
      "Pay via UPI / card / bank transfer (traceable) — avoid cash for large amounts",
      "Invoice must show: weight, purity, HUID, rate/gram, making charges, 3% GST, seller GSTIN",
      "Match invoice weight & HUID with the piece before leaving",
    ],
    documents: ["GST invoice (itemised)"],
  },
  {
    title: "After purchase",
    why: "Protect the asset — resale value depends on paperwork + condition.",
    checkpoints: [
      "Store invoice + assay card safely (photo backup)",
      "Bank locker or home safe; insure holdings above ₹5 lakh",
      "Re-verify HUID in BIS Care app at home",
    ],
    documents: [],
  },
];

const LAND_STEPS = [
  {
    title: "Site visit & water check",
    why: "Cheap land without water is a liability, not an asset.",
    checkpoints: [
      "Visit physically — verify access road, electricity line nearby",
      "Ask neighbouring farmers: borewell depth & yield in the area",
      "Look for canal / tank / river within reach; check monsoon vs summer condition",
    ],
    documents: [],
  },
  {
    title: "Ask the seller for documents",
    why: "This list filters out 90% of problem deals before you spend on lawyers.",
    checkpoints: [
      "All documents below received as copies for verification",
      "Seller's ID matches the name on the title documents",
      "If inherited property: all legal heirs available to sign",
    ],
    documents: [
      "Title deed + mother deed chain (30 years)",
      "Encumbrance Certificate (EC) — 30 years",
      "RTC / 7-12 extract / Patta (land records)",
      "Survey sketch & boundaries map",
      "Latest land-tax paid receipts",
      "Seller's Aadhaar + PAN",
      "Family tree / legal-heir certificate (if inherited)",
      "Conversion order (only if converted land)",
    ],
  },
  {
    title: "Independent verification",
    why: "Never rely on the seller's copies alone.",
    checkpoints: [
      "Pull a fresh EC yourself at the sub-registrar office / online portal",
      "Match survey number & extent at the village revenue office",
      "Licensed surveyor marks boundaries on the ground",
      "Check your state's agri-land buyer eligibility & land-ceiling rules",
      "Litigation search (court records / local lawyer)",
    ],
    documents: ["Fresh EC (self-obtained)", "Surveyor's report"],
  },
  {
    title: "Water & soil validation",
    why: "Prove the water before you pay for it.",
    checkpoints: [
      "Hydrogeologist survey or borewell yield test on the plot",
      "Soil test for intended crops",
      "Groundwater authority NOC if the area is notified",
    ],
    documents: ["Borewell yield / hydrogeologist report", "Soil test report"],
  },
  {
    title: "Agreement to sell",
    why: "Locks the price and gives you time to finish checks.",
    checkpoints: [
      "Registered Agreement to Sell drafted by YOUR lawyer",
      "Token amount ≤ 5-10% with clear refund clauses",
      "Seller indemnity clause for title defects",
      "Clear timeline for registration",
    ],
    documents: ["Registered Agreement to Sell"],
  },
  {
    title: "Registration day",
    why: "The sale deed at the sub-registrar makes it legally yours.",
    checkpoints: [
      "Stamp duty & registration fee paid at government value or above",
      "All sellers + witnesses present with ID; biometric done",
      "Collect the registered sale deed (or acknowledgement)",
    ],
    documents: ["Registered sale deed"],
  },
  {
    title: "Mutation & possession",
    why: "Registration alone isn't enough — revenue records must show your name.",
    checkpoints: [
      "Apply for mutation (khata / patta transfer) immediately",
      "Fence / mark boundaries with the surveyor present",
      "Land-tax record updated to your name",
    ],
    documents: ["Mutation certificate / updated khata"],
  },
];

export default function PurchaseWizard({ type, onClose }) {
  // Normalise metals to 'gold' checklist (silver reuses the same 6-step flow but chat/vault/UPI stay silver-specific)
  const flowType = type === "silver" ? "gold" : type;
  const steps = flowType === "gold" ? GOLD_STEPS : LAND_STEPS;
  const storageKey = `wizard_${type}`;
  const [done, setDone] = useState({});
  const [leadLocation, setLeadLocation] = useState("");
  const [leadSending, setLeadSending] = useState(false);
  const [tab, setTab] = useState("checklist"); // checklist | chat | pay | vault
  const [activeStep, setActiveStep] = useState(0);

  useEffect(() => {
    try {
      setDone(JSON.parse(localStorage.getItem(storageKey) || "{}"));
    } catch {
      setDone({});
    }
  }, [storageKey]);

  const toggle = (key) => {
    setDone((d) => {
      const next = { ...d, [key]: !d[key] };
      localStorage.setItem(storageKey, JSON.stringify(next));
      return next;
    });
  };

  const totals = useMemo(() => {
    let total = 0;
    steps.forEach((s, si) => (total += s.checkpoints.length));
    const completed = Object.values(done).filter(Boolean).length;
    return { total, completed };
  }, [steps, done]);

  const sendLead = async () => {
    if (leadSending) return;
    setLeadSending(true);
    try {
      await api.post("/leads", { type, location: leadLocation || null, note: `assisted-${type}-wizard` });
      toast.success("Request received — we'll connect you with verified sellers");
    } catch {
      toast.error("Could not send — try again");
    } finally {
      setLeadSending(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[60] bg-stone_brand-dark/50 backdrop-blur-sm flex items-start justify-center overflow-y-auto p-2 sm:p-8" data-testid={`purchase-wizard-${type}`} onClick={onClose}>
      <div className="bg-bone-50 border border-line rounded-sm max-w-3xl w-full my-2 sm:my-4" onClick={(e) => e.stopPropagation()}>
        <div className="px-4 sm:px-6 py-3 sm:py-4 border-b border-line flex items-start justify-between sticky top-0 bg-bone-50 z-10">
          <div className="flex items-start gap-2 sm:gap-3 min-w-0 flex-1">
            {(() => {
              const meta = WIZARD_META[type] || WIZARD_META.gold;
              const Icon = meta.Icon;
              return (
                <span
                  className={`inline-flex items-center justify-center w-9 h-9 sm:w-11 sm:h-11 rounded-sm ${meta.bg} ${meta.accent} ring-1 ${meta.ring} flex-shrink-0 mt-0.5`}
                  aria-hidden="true"
                >
                  <Icon className="w-4 h-4 sm:w-5 sm:h-5" strokeWidth={2} />
                </span>
              );
            })()}
            <div className="min-w-0 flex-1">
              <div className="overline mb-1 text-moss text-[0.6rem] sm:text-[0.65rem]">— Assisted purchase</div>
              <div className="font-serif-display text-lg sm:text-2xl text-stone_brand-dark font-medium leading-tight">
                {(WIZARD_META[type] || WIZARD_META.gold).title}
              </div>
              <div className="text-[0.7rem] sm:text-xs font-mono text-stone_brand mt-1" data-testid="wizard-progress">
                {totals.completed}/{totals.total} checkpoints done
              </div>
            </div>
          </div>
          <button onClick={onClose} className="p-1.5 text-stone_brand-light hover:text-terracotta rounded-sm flex-shrink-0 ml-2" data-testid="wizard-close">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Tabs */}
        <div className="px-3 sm:px-6 pt-3 border-b border-line bg-bone-50 sticky top-[80px] sm:top-[92px] z-10 overflow-x-auto">
          <div className="flex gap-1 min-w-max sm:flex-wrap" data-testid="wizard-tabs">
            {[
              { id: "checklist", label: "Checklist", icon: ListChecks },
              { id: "chat", label: "Guide", icon: MessageSquare },
              { id: "pay", label: "UPI", icon: Wallet },
              { id: "vault", label: "Vault", icon: Archive },
            ].map(({ id, label, icon: Icon }) => (
              <button
                key={id}
                onClick={() => setTab(id)}
                className={
                  "px-2.5 sm:px-3 py-2 text-[0.65rem] sm:text-xs font-mono uppercase tracking-wider inline-flex items-center gap-1.5 border-b-2 -mb-px transition whitespace-nowrap " +
                  (tab === id
                    ? "border-moss text-stone_brand-dark"
                    : "border-transparent text-stone_brand-light hover:text-stone_brand")
                }
                data-testid={`wizard-tab-${id}`}
              >
                <Icon className="w-3.5 h-3.5" />
                {label}
              </button>
            ))}
          </div>
        </div>

        {tab === "checklist" && (
          <div className="px-4 sm:px-6 py-4 sm:py-5 space-y-4 sm:space-y-6">
            {steps.map((s, si) => (
              <div
                key={si}
                className="bg-white border border-line rounded-sm p-5"
                data-testid={`wizard-step-${si}`}
                onFocus={() => setActiveStep(si)}
                onClick={() => setActiveStep(si)}
              >
              <div className="flex items-baseline gap-2 mb-1">
                <span className="font-mono text-xs text-moss">STEP {si + 1}</span>
                <span className="font-serif-display text-lg text-stone_brand-dark font-medium">{s.title}</span>
              </div>
              <p className="text-xs text-stone_brand mb-3">{s.why}</p>
              <ul className="space-y-1.5 mb-3">
                {s.checkpoints.map((c, ci) => {
                  const key = `${si}-${ci}`;
                  return (
                    <li key={ci}>
                      <label className="flex items-start gap-2 text-sm text-stone_brand-dark cursor-pointer group">
                        <input
                          type="checkbox"
                          checked={!!done[key]}
                          onChange={() => toggle(key)}
                          className="mt-0.5 accent-[#3f5c40]"
                          data-testid={`wizard-check-${key}`}
                        />
                        <span className={done[key] ? "line-through text-stone_brand-light" : ""}>{c}</span>
                      </label>
                    </li>
                  );
                })}
              </ul>
              {s.documents.length > 0 && (
                <div>
                  <div className="text-[0.65rem] font-mono uppercase tracking-widest text-amber_brand mb-1.5 flex items-center gap-1">
                    <FileText className="w-3 h-3" /> Documents to collect from the seller
                  </div>
                  <div className="flex flex-wrap gap-1.5">
                    {s.documents.map((d, di) => (
                      <span key={di} className="text-[0.7rem] bg-amber_brand-bg text-stone_brand-dark border border-amber_brand/30 px-2 py-1 rounded-sm">
                        {d}
                      </span>
                    ))}
                  </div>
                </div>
              )}
              <div className="mt-3">
                <button
                  onClick={() => {
                    setActiveStep(si);
                    setTab("chat");
                  }}
                  className="text-[0.7rem] font-mono uppercase tracking-wider text-moss hover:text-moss-hover inline-flex items-center gap-1"
                  data-testid={`wizard-ask-guide-${si}`}
                >
                  <MessageSquare className="w-3 h-3" /> Ask the guide about this step
                </button>
              </div>
            </div>
          ))}

            <div className="bg-moss-bg/50 border border-moss/30 rounded-sm p-5" data-testid="wizard-seller-connect">
              <div className="flex items-center gap-2 mb-1.5">
                <Handshake className="w-4 h-4 text-moss" />
                <span className="font-serif-display text-lg text-stone_brand-dark font-medium">Want us to connect you with verified sellers?</span>
              </div>
              <p className="text-xs text-stone_brand mb-3">
                Free for you — tell us your city and we'll match you with vetted {type === "land" ? "land aggregators & managed-farmland operators" : type === "silver" ? "silver refiners & bullion dealers" : "bullion dealers & refiners"}.
              </p>
              <div className="flex flex-wrap gap-2">
                <input
                  value={leadLocation}
                  onChange={(e) => setLeadLocation(e.target.value)}
                  placeholder="Your city (optional)"
                  className="h-10 px-3 text-sm rounded-sm border border-line bg-white focus:outline-none focus:ring-1 focus:ring-moss min-w-[180px]"
                  data-testid="wizard-lead-location"
                />
                <Button onClick={sendLead} disabled={leadSending} className="rounded-sm bg-moss hover:bg-moss-hover text-bone-50 h-10 gap-1.5" data-testid="wizard-lead-btn">
                  <CheckCircle2 className="w-4 h-4" /> {leadSending ? "Sending…" : "Connect me — free"}
                </Button>
              </div>
            </div>
          </div>
        )}

        {tab === "chat" && (
          <div className="px-3 sm:px-6 py-4 sm:py-5">
            <PurchaseChat
              type={type}
              currentStep={steps[activeStep]?.title || null}
              context={{ location: leadLocation || null }}
            />
          </div>
        )}

        {tab === "pay" && (
          <div className="px-3 sm:px-6 py-4 sm:py-5">
            <UpiPayPanel type={type} />
          </div>
        )}

        {tab === "vault" && (
          <div className="px-3 sm:px-6 py-4 sm:py-5">
            <PurchaseVault type={type} />
          </div>
        )}
      </div>
    </div>
  );
}
